import { API_BASE_URL } from '../api';
import type { ApiResponse } from '../types/ApiResponse';
import { translateErrorCode } from '../i18n/error_codes';
import { authManager } from '../services/AuthManager';

/**
 * Repräsentiert einen Fehler, der bei API-Requests auftritt.
 * Enthält den HTTP-Statuscode, den Antwort-Body und das ursprüngliche Response-Objekt.
 * Wird verwendet, um Fehler detailliert an das UI weiterzugeben.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  response: Response;
  /** Optional machine-readable error code from the backend wrapper */
  errorCode?: string;
  /** Optional parameters for templated error messages */
  errorParams?: Record<string, unknown> | null | undefined;

  /**
   * Erstellt eine neue Instanz von ApiError.
   * @param message - Technische Meldung (nur Fallback/Debug; UI soll anhand errorCode übersetzen).
   * @param status - HTTP-Statuscode der Antwort.
   * @param body - Inhalt der Antwort (kann beliebige Struktur haben).
   * @param response - Originales Response-Objekt der Fetch-Anfrage.
   * @param errorCode - (optional) maschinenlesbarer Fehlercode.
   * @param errorParams - (optional) Parameter für Fehlermeldungen.
   */
  constructor(
    message: string,
    status: number,
    body: unknown,
    response: Response,
    errorCode?: string,
    errorParams?: Record<string, unknown> | null
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.response = response;
    this.errorCode = errorCode;
    this.errorParams = errorParams ?? undefined;
  }
}

/**
 * Optionale Zusatzoptionen zu RequestInit für API-Requests.
 */
export interface ApiRequestOptions extends RequestInit {
  /** Query-Parameter, die an die URL gehängt werden. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Wenn gesetzt wird das Objekt als JSON gesendet (setzt automatisch content-type) */
  json?: unknown;
  /** Token explizit übergeben; wenn nicht gesetzt, wird getAuthToken() genutzt. */
  token?: string | null;
  /** Auth standardmäßig AN; auf false setzen, um keinen Authorisation-Header zu senden. */
  auth?: boolean;
}
/**
 * Baut einen Query-String aus einem flachen Objekt.
 * @param query - Objekt mit Query-Parametern.
 * @returns {string} Ein URL-kodierter Query-String, beginnend mit '?' oder leerer String, falls keine Parameter.
 */
function buildQueryString(query?: ApiRequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    params.append(key, typeof value === 'boolean' ? String(value) : String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Type guard: checks if an arbitrary value already looks like our ResponseWrapper */
function looksLikeResponseWrapper(v: unknown): v is ApiResponse<unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    (Object.hasOwn(v as Record<string, unknown>, 'ok') ||
      Object.hasOwn(v as Record<string, unknown>, 'status_code') ||
      Object.hasOwn(v as Record<string, unknown>, 'statusCode') ||
      Object.hasOwn(v as Record<string, unknown>, 'data') ||
      Object.hasOwn(v as Record<string, unknown>, 'error') ||
      Object.hasOwn(v as Record<string, unknown>, 'error_code'))
  );
}

/**
 * Normalize any parsed JSON into our ApiResponse<T> shape without breaking existing callers.
 * - If the payload already follows the wrapper, it is returned (with light coercion only).
 * - If it's a bare payload (legacy), we wrap it into `{ ok, status_code, data }`.
 */
export function toApiResponse<T>(raw: unknown, status: number): ApiResponse<T> {
  if (looksLikeResponseWrapper(raw)) {
    const r = raw as ApiResponse<T>;
    if (typeof r.ok !== 'boolean') {
      r.ok = status >= 200 && status < 300 && !r.error;
    }
    if (r.status_code == null) {
      const maybeStatusCode = (r as Partial<{ statusCode: number }>).statusCode;
      if (maybeStatusCode != null) {
        r.status_code = maybeStatusCode;
      }
    }
    return r;
  }
  return {
    ok: status >= 200 && status < 300,
    status_code: status,
    // Legacy callers of apiRequest<T> expect T directly – the wrapped variants will read from .data
    data: raw as T,
    // Legacy fields kept for compatibility; callers of wrapped API should prefer the canonical ones
    statusCode: status,
    message: '',
    error: null,
  };
}

/**
 * Convenience: Extract a user-facing message from an ApiResponse.
 * Falls back to the first detail.message or a generic HTTP message.
 */
export function pickResponseMessage(resp: ApiResponse<unknown>, fallback?: string): string | undefined {
  if (resp.message) return resp.message;
  if (resp.details && resp.details.length) {
    const first = resp.details.find((d) => d?.message) || resp.details[0];
    if (first?.message) return first.message;
  }
  if (resp.error) return resp.error;
  return fallback;
}

/** Shorthand: true if 2xx and ok !== false */
export function isOkResponse(resp: ApiResponse<unknown>, status: number): boolean {
  if (typeof resp.ok === 'boolean') return resp.ok;
  return status >= 200 && status < 300 && !resp.error_code && !resp.error;
}

/**
 * Zentraler API-Client für JSON basierte Requests.
 *
 * Beispiel:
 *  const data = await apiRequest<UserWithProfileRead>('/families/me', {method: 'GET'});
 *
 * @template T - Erwarteter Rückgabetyp des API-Requests.
 * @param path - Pfad der API-Route (wird an API_BASE_URL angehängt).
 * @param options - Zusätzliche Optionen für den Request (Query, JSON-Body, Auth etc.).
 * @returns {Promise<T>} Die Antwort des API-Requests als Typ T.
 * @throws {ApiError} Wirft einen ApiError bei HTTP-Fehlern.
 * @note Returns the **payload only** (legacy-friendly). Callers who need wrapper metadata should use the `...Wrapped` variants.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { query, json, token, auth = true, headers: headersIn, ...rest } = options;

  const base = API_BASE_URL.replace(/\/+$/, '');
  const url = `${base}${path}${buildQueryString(query)}`;

  // Header zusammenbauen
  const headers = new Headers(headersIn || {});
  headers.set('Accept', 'application/json');

  // Authorization (opt-out über auth:false)
  if (auth) {
    const bearer = token ?? authManager.getAccessToken();
    if (bearer) headers.set('Authorization', `Bearer ${bearer}`);
  }

  // Body/Content-Type setzen:
  let body: BodyInit | undefined = rest.body as BodyInit | undefined;

  if (json !== undefined) {
    body = JSON.stringify(json);
    headers.set('Content-Type', 'application/json');
  } else if (body && !(body instanceof FormData)) {
    // Wenn ein Body gesetzt ist, aber kein FormData -> JSON annehmen (falls nichts anderes angegeben)
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }
  // Bei FormData niemals selbst 'Content-Tyoe' setzten, der Browser macht das inkl. boundary!

  let res = await fetch(url, {
    ...rest,
    headers,
    body,
  });

  if (res.status === 401 && auth) {
    const newToken = await authManager.refreshAccessToken();

    if (!newToken) {
      authManager.logoutAndRedirect();
      throw new ApiError('Unauthorised', res.status, null, res, 'UNAUTHORIZED', null);
    }

    headers.set('Authorization', `Bearer ${newToken}`);

    res = await fetch(url, {
      ...rest,
      headers,
      body,
    });
  }

  // Content-Type prüfen, um zu entscheiden, wie wir parsen
  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');

  // Response-Body (auch bei Fehlern) lesbar machen
  let parsed: unknown = undefined;
  try {
    if (res.status !== 204) {
      parsed = isJson ? await res.json() : await res.text();
    }
  } catch {
    // Parsing kann schiefgehen (leerer Body bei 200, nicht-JSON Content etc.) -> ignorieren
    parsed = undefined;
  }

  if (!res.ok) {
    // Versuche Wrapper-Fehlerinformationen zu lesen und direkt zu übersetzen
    let code: string | undefined;
    let params: Record<string, unknown> | undefined;
    let message: string | undefined;

    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      code = typeof o.error_code === 'string' ? (o.error_code as string) : undefined;
      params = (o.error_params ?? undefined) as Record<string, unknown> | undefined;
      message = typeof o.message === 'string' ? (o.message as string) : undefined;
    } else if (typeof parsed === 'string') {
      message = parsed;
    }

    const uiMsg = translateErrorCode(code, params, message) || `HTTP ${res.status}`;

    throw new ApiError(uiMsg, res.status, parsed, res, code, params ?? null);
  }

  // Erfolgsfall: T zurückgeben (JSON oder Text). 204 -> undefined as unknown as T.
  return (parsed as T) ?? (undefined as unknown as T);
}

/**
 * Shortcut für GET-Requests an die API.
 *
 * @template T - Erwarteter Rückgabetyp des API-Requests.
 * @param path - Pfad der API-Route.
 * @param opts - Optionale Request-Optionen ohne 'method' und 'json'.
 * @returns {Promise<T>} Die Antwort des API-Requests als Typ T.
 */
export const apiGet = <T>(path: string, opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}): Promise<T> =>
  apiRequest<T>(path, {
    ...opts,
    method: 'GET',
  });

/**
 * Shortcut für POST-Requests an die API mit JSON-Body.
 *
 * @template T - Erwarteter Rückgabetyp des API-Requests.
 * @param path - Pfad der API-Route.
 * @param json - JSON-Objekt, das im Body gesendet wird.
 * @param opts - Optionale Request-Optionen ohne 'method' und 'json'.
 * @returns {Promise<T>} Die Antwort des API-Requests als Typ T.
 */
export const apiPost = <T>(
  path: string,
  json?: unknown,
  opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}
): Promise<T> => apiRequest<T>(path, { ...opts, method: 'POST', json });

/**
 * Shortcut für PUT-Requests an die API mit JSON-Body.
 *
 * @template T - Erwarteter Rückgabetyp des API-Requests.
 * @param path - Pfad der API-Route.
 * @param json - JSON-Objekt, das im Body gesendet wird.
 * @param opts - Optionale Request-Optionen ohne 'method' und 'json'.
 * @returns {Promise<T>} Die Antwort des API-Requests als Typ T.
 */
export const apiPut = <T>(
  path: string,
  json?: unknown,
  opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}
): Promise<T> => apiRequest<T>(path, { ...opts, method: 'PUT', json });

/**
 * Shortcut für PATCH-Requests an die API mit JSON-Body.
 *
 * @template T - Erwarteter Rückgabetyp des API-Requests.
 * @param path - Pfad der API-Route.
 * @param json - JSON-Objekt, das im Body gesendet wird.
 * @param opts - Optionale Request-Optionen ohne 'method' und 'json'.
 * @returns {Promise<T>} Die Antwort des API-Requests als Typ T.
 */
export const apiPatch = <T>(
  path: string,
  json?: unknown,
  opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}
): Promise<T> => apiRequest<T>(path, { ...opts, method: 'PATCH', json });

/**
 * Shortcut für DELETE-Requests an die API.
 *
 * @template T - Erwarteter Rückgabetyp des API-Requests.
 * @param path - Pfad der API-Route.
 * @param opts - Optionale Request-Optionen ohne 'method' und 'json'.
 * @returns {Promise<T>} Die Antwort des API-Requests als Typ T.
 */
export const apiDelete = <T>(path: string, opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}): Promise<T> =>
  apiRequest<T>(path, { ...opts, method: 'DELETE' });

/**
 * Wrapped request: Gibt IMMER ApiResponse<T> zurück.
 *  - 2xx → resp.ok true (oder vom Backend gesetzt)
 *  - non-2xx → KEIN throw; wir liefern resp.ok=false zurück
 *  So kann die UI zentral via translateErrorCode(resp.error_code, resp.error_params, resp.message) übersetzen.
 */
export async function apiRequestWrapped<T>(path: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
  const { query, json, token, auth = true, headers: headersIn, ...rest } = options;

  const base = API_BASE_URL.replace(/\/+$/, '');
  const url = `${base}${path}${buildQueryString(query)}`;

  const headers = new Headers(headersIn || {});
  headers.set('Accept', 'application/json');

  if (auth) {
    const bearer = token ?? authManager.getAccessToken();
    if (bearer) headers.set('Authorization', `Bearer ${bearer}`);
  }

  let body: BodyInit | undefined = rest.body as BodyInit | undefined;

  if (json !== undefined) {
    body = JSON.stringify(json);
    headers.set('Content-Type', 'application/json');
  } else if (body && !(body instanceof FormData)) {
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  }

  let res = await fetch(url, { ...rest, headers, body });

  if (res.status === 401 && auth) {
    const newToken = await authManager.refreshAccessToken();

    if (!newToken) {
      authManager.logoutAndRedirect();
      return toApiResponse<T>(
        {
          ok: false,
          status_code: 401,
          error_code: 'UNAUTHORIZED',
          error_params: null,
          message: 'Unauthorized',
        },
        401
      );
    }

    headers.set('Authorization', `Bearer ${newToken}`);

    res = await fetch(url, {
      ...rest,
      headers,
      body,
    });
  }

  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');

  let parsed: unknown = undefined;
  try {
    if (res.status !== 204) parsed = isJson ? await res.json() : await res.text();
  } catch {
    parsed = undefined;
  }

  const wrapped = toApiResponse<T>(parsed, res.status);

  if (!res.ok) {
    // Für wrapped-Varianten NICHT werfen: UI soll `resp.ok` prüfen und übersetzen
    wrapped.ok = false;
    return wrapped;
  }

  return wrapped;
}

/**
 * @returns ApiResponse<T> (ResponseWrapper). Prefer diese Funktionen, wenn du mit ok/message/details arbeiten willst.
 * Bestehende Nutzer können bei apiGet/apiPost/... bleiben (Payload-only)
 */
export const apiGetWrapped = <T>(path: string, opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}) =>
  apiRequestWrapped<T>(path, { ...opts, method: 'GET' });

export const apiPostWrapped = <T>(
  path: string,
  json?: unknown,
  opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}
) => apiRequestWrapped<T>(path, { ...opts, method: 'POST', json });

export const apiPutWrapped = <T>(path: string, json?: unknown, opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}) =>
  apiRequestWrapped<T>(path, { ...opts, method: 'PUT', json });

export const apiPatchWrapped = <T>(
  path: string,
  json?: unknown,
  opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}
) => apiRequestWrapped<T>(path, { ...opts, method: 'PATCH', json });

export const apiDeleteWrapped = <T>(path: string, opts: Omit<ApiRequestOptions, 'method' | 'json'> = {}) =>
  apiRequestWrapped<T>(path, { ...opts, method: 'DELETE' });

/**
 * Download-Helfer für binäre Antworten (z.B. PDF, Bilder) als Blob.
 *
 * Verwendet die gleiche Auth- und Query-Handling-Logik wie apiRequest.
 * Standardmäßig wird eine GET-Anfrage ausgeführt und ein Authorization-Header gesendet,
 * sofern nicht `auth: false` gesetzt ist.
 *
 * @param path - Pfad der API-Route.
 * @param opts - Optionale Request-Optionen ohne 'json'.
 * @returns {Promise<Blob>} Das heruntergeladene Blob-Objekt.
 * @throws {ApiError} Wirft einen ApiError bei HTTP-Fehlern.
 */
export const apiGetBlob = async (path: string, opts: Omit<ApiRequestOptions, 'json'> = {}): Promise<Blob> => {
  const { query, token, auth = true, headers: headersIn, ...rest } = opts;

  const base = API_BASE_URL.replace(/\/+$/, '');
  const url = `${base}${path}${buildQueryString(query)}`;

  const headers = new Headers(headersIn || {});
  if (!headers.has('Accept')) headers.set('Accept', '*/*');

  if (auth) {
    const bearer = token ?? authManager.getAccessToken();
    if (bearer) headers.set('Authorization', `Bearer ${bearer}`);
  }

  const res = await fetch(url, {
    ...rest,
    method: rest.method ?? 'GET',
    headers,
  });

  if (!res.ok) {
    // Versuche JSON zu lesen, um Wrapper-Fehler zu extrahieren und zu übersetzen
    let bodyTxt: string | undefined;
    try {
      bodyTxt = await res.text();
    } catch {
      bodyTxt = undefined;
    }

    let uiMsg = `HTTP ${res.status}`;
    let code: string | undefined;
    let params: Record<string, unknown> | undefined;
    let parsedJson: unknown | undefined;

    if (bodyTxt) {
      try {
        parsedJson = JSON.parse(bodyTxt);
        if (parsedJson && typeof parsedJson === 'object') {
          const o = parsedJson as Record<string, unknown>;
          const maybeCode = o['error_code'];
          code = typeof maybeCode === 'string' ? maybeCode : undefined;

          const maybeParams = o['error_params'];
          params = (maybeParams as Record<string, unknown> | undefined) ?? undefined;

          const maybeMsg = o['message'];
          const message = typeof maybeMsg === 'string' ? maybeMsg : undefined;

          uiMsg = translateErrorCode(code, params, message) || uiMsg;
        } else {
          uiMsg = bodyTxt;
        }
      } catch {
        // kein JSON, nutze Text direkt
        uiMsg = bodyTxt;
      }
    }

    throw new ApiError(uiMsg, res.status, parsedJson ?? bodyTxt, res, code, params ?? null);
  }

  return await res.blob();
};
